import { supabase } from '../lib/supabase';

// ============================================================
// NOTIFICATION STAGE CONFIG
// Maps each workflow stage to a message template and action
// ============================================================

export const NOTIFICATION_STAGES = {
  'Pending Team Assignment': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" is pending team assignment. Please assign the project team members.`,
    actionLabel: 'Assign Team',
  },
  'Calculated': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" has been calculated and is ready for approval submission.`,
    actionLabel: 'Review & Submit',
  },
  'Pending Approval': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" is awaiting your approval. Please review and take action.`,
    actionLabel: 'Review & Approve',
  },
  'Approved': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" has been approved and is ready for payment processing.`,
    actionLabel: 'Process Payment',
  },
  'Ready for Payment': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" is ready for payment. Please mark it as paid once processed.`,
    actionLabel: 'Mark as Paid',
  },
  'Paid': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" has been marked as paid. Incentives have been disbursed.`,
    actionLabel: 'View Details',
  },
  'Rejected': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" has been rejected. Please review the comments and resubmit.`,
    actionLabel: 'View Details',
  },
  'On Hold': {
    message: (ticketNumber, projectName) =>
      `Ticket ${ticketNumber} for "${projectName}" has been placed on hold. Review the reason and take action.`,
    actionLabel: 'View Details',
  },
};

// ============================================================
// CREATE NOTIFICATION
// ============================================================

export const createNotification = async ({
  ticketId,
  ticketNumber,
  projectName,
  recipientUserId,
  triggeredByUserId,
  stage,
  customMessage = null,
}) => {
  if (!recipientUserId || !ticketId) return null;

  const stageConfig = NOTIFICATION_STAGES?.[stage];
  if (!stageConfig) return null;

  const message = customMessage || stageConfig?.message(ticketNumber, projectName);
  const actionLabel = stageConfig?.actionLabel;
  const actionUrl = `/incentive-tickets/${ticketId}`;

  try {
    const { data, error } = await supabase
      ?.from('incentive_notifications')
      ?.insert([{
        ticket_id: ticketId,
        ticket_number: ticketNumber,
        project_name: projectName,
        recipient_user_id: recipientUserId,
        triggered_by_user_id: triggeredByUserId || null,
        stage,
        message,
        action_label: actionLabel,
        action_url: actionUrl,
        is_read: false,
      }])
      ?.select()
      ?.single();

    if (error) {
      console.warn('[Notifications] Failed to create notification:', error?.message);
      return null;
    }
    return data;
  } catch (err) {
    console.warn('[Notifications] Error creating notification:', err?.message);
    return null;
  }
};

// ============================================================
// BULK CREATE NOTIFICATIONS (for multiple recipients)
// ============================================================

export const createNotificationsForUsers = async ({
  ticketId,
  ticketNumber,
  projectName,
  recipientUserIds,
  triggeredByUserId,
  stage,
}) => {
  if (!recipientUserIds?.length || !ticketId) return [];

  const stageConfig = NOTIFICATION_STAGES?.[stage];
  if (!stageConfig) return [];

  const message = stageConfig?.message(ticketNumber, projectName);
  const actionLabel = stageConfig?.actionLabel;
  const actionUrl = `/incentive-tickets/${ticketId}`;

  // Deduplicate and exclude the triggering user from receiving their own notification
  const uniqueRecipients = [...new Set(recipientUserIds)]?.filter(
    uid => uid && uid !== triggeredByUserId
  );

  if (!uniqueRecipients?.length) return [];

  const rows = uniqueRecipients?.map(uid => ({
    ticket_id: ticketId,
    ticket_number: ticketNumber,
    project_name: projectName,
    recipient_user_id: uid,
    triggered_by_user_id: triggeredByUserId || null,
    stage,
    message,
    action_label: actionLabel,
    action_url: actionUrl,
    is_read: false,
  }));

  try {
    const { data, error } = await supabase
      ?.from('incentive_notifications')
      ?.insert(rows)
      ?.select();

    if (error) {
      console.warn('[Notifications] Bulk create error:', error?.message);
      return [];
    }
    return data || [];
  } catch (err) {
    console.warn('[Notifications] Bulk create exception:', err?.message);
    return [];
  }
};

// ============================================================
// FETCH NOTIFICATIONS FOR CURRENT USER
// ============================================================

export const fetchMyNotifications = async ({ limit = 50, unreadOnly = false } = {}) => {
  let query = supabase
    ?.from('incentive_notifications')
    ?.select('*')
    ?.order('created_at', { ascending: false })
    ?.limit(limit);

  if (unreadOnly) {
    query = query?.eq('is_read', false);
  }

  const { data, error } = await query;
  if (error) {
    console.warn('[Notifications] Fetch error:', error?.message);
    return [];
  }
  return data || [];
};

// ============================================================
// MARK NOTIFICATION AS READ
// ============================================================

export const markNotificationRead = async (notificationId) => {
  const { error } = await supabase
    ?.from('incentive_notifications')
    ?.update({ is_read: true, read_at: new Date()?.toISOString() })
    ?.eq('id', notificationId);

  if (error) console.warn('[Notifications] Mark read error:', error?.message);
};

// ============================================================
// MARK ALL NOTIFICATIONS AS READ
// ============================================================

export const markAllNotificationsRead = async () => {
  const { error } = await supabase
    ?.from('incentive_notifications')
    ?.update({ is_read: true, read_at: new Date()?.toISOString() })
    ?.eq('is_read', false);

  if (error) console.warn('[Notifications] Mark all read error:', error?.message);
};

// ============================================================
// DELETE NOTIFICATION
// ============================================================

export const deleteNotification = async (notificationId) => {
  const { error } = await supabase
    ?.from('incentive_notifications')
    ?.delete()
    ?.eq('id', notificationId);

  if (error) console.warn('[Notifications] Delete error:', error?.message);
};

// ============================================================
// GET UNREAD COUNT
// ============================================================

export const getUnreadNotificationCount = async () => {
  const { count, error } = await supabase
    ?.from('incentive_notifications')
    ?.select('*', { count: 'exact', head: true })
    ?.eq('is_read', false);

  if (error) return 0;
  return count || 0;
};

// ============================================================
// FETCH USERS BY ROLE (to find recipients for each stage)
// ============================================================

export const fetchUsersByRole = async (roleKeys = []) => {
  if (!roleKeys?.length) return [];
  try {
    // Get user_role_assignments joined with roles
    const { data, error } = await supabase
      ?.from('user_role_assignments')
      ?.select(`
        user_id,
        roles!inner(role_key)
      `)
      ?.in('roles.role_key', roleKeys);

    if (error) {
      console.warn('[Notifications] fetchUsersByRole error:', error?.message);
      return [];
    }
    return (data || [])?.map(r => r?.user_id)?.filter(Boolean);
  } catch (err) {
    console.warn('[Notifications] fetchUsersByRole exception:', err?.message);
    return [];
  }
};

// ============================================================
// FETCH ASSIGNED USERS FOR A STAGE (specific user assignments)
// ============================================================

export const fetchStageAssignedUserIds = async (stageId) => {
  if (!stageId) return [];
  try {
    const { data, error } = await supabase
      ?.from('workflow_stage_user_assignments')
      ?.select('user_id')
      ?.eq('stage_id', stageId);

    if (error) {
      console.warn('[Notifications] fetchStageAssignedUserIds error:', error?.message);
      return [];
    }
    return (data || [])?.map(r => r?.user_id)?.filter(Boolean);
  } catch (err) {
    console.warn('[Notifications] fetchStageAssignedUserIds exception:', err?.message);
    return [];
  }
};

// ============================================================
// SAVE STAGE USER ASSIGNMENTS
// Replaces all assignments for a given stage with the new list
// ============================================================

export const saveStageUserAssignments = async (stageId, userIds = [], assignedByUserId = null) => {
  if (!stageId) return false;
  try {
    // Delete existing assignments for this stage
    const { error: deleteError } = await supabase
      ?.from('workflow_stage_user_assignments')
      ?.delete()
      ?.eq('stage_id', stageId);

    if (deleteError) {
      console.warn('[Notifications] saveStageUserAssignments delete error:', deleteError?.message);
      return false;
    }

    // Insert new assignments (if any)
    if (userIds?.length > 0) {
      const rows = userIds?.map(uid => ({
        stage_id: stageId,
        user_id: uid,
        assigned_by: assignedByUserId || null,
      }));

      const { error: insertError } = await supabase
        ?.from('workflow_stage_user_assignments')
        ?.insert(rows);

      if (insertError) {
        console.warn('[Notifications] saveStageUserAssignments insert error:', insertError?.message);
        return false;
      }
    }

    return true;
  } catch (err) {
    console.warn('[Notifications] saveStageUserAssignments exception:', err?.message);
    return false;
  }
};

// ============================================================
// FETCH ALL STAGE ASSIGNMENTS WITH USER PROFILES
// Returns a map: stageId -> [{ user_id, full_name, email }]
// ============================================================

export const fetchAllStageAssignments = async () => {
  try {
    const { data, error } = await supabase
      ?.from('workflow_stage_user_assignments')
      ?.select(`
        stage_id,
        user_id,
        user_profiles!workflow_stage_user_assignments_user_id_fkey(id, full_name, email)
      `);

    if (error) {
      console.warn('[Notifications] fetchAllStageAssignments error:', error?.message);
      return {};
    }

    const map = {};
    (data || [])?.forEach(row => {
      const sid = row?.stage_id;
      if (!map?.[sid]) map[sid] = [];
      if (row?.user_profiles) {
        map?.[sid]?.push({
          id: row?.user_id,
          full_name: row?.user_profiles?.full_name,
          email: row?.user_profiles?.email,
        });
      }
    });
    return map;
  } catch (err) {
    console.warn('[Notifications] fetchAllStageAssignments exception:', err?.message);
    return {};
  }
};

// ============================================================
// TRIGGER WORKFLOW NOTIFICATION
// Determines the right recipients:
//   1. First checks for specific user assignments for the stage
//   2. Falls back to role-based notification if no specific users assigned
// ============================================================

export const triggerWorkflowNotification = async ({
  ticketId,
  ticketNumber,
  projectName,
  newStatus,
  triggeredByUserId,
  ticketCreatorId = null,
  salesOwnerUserId = null,
  projectMemberUserIds = [],
}) => {
  try {
    let recipientUserIds = [];

    // ── Step 1: Check for specific user assignments for this stage ──────────
    const specificUserIds = await fetchStageAssignedUserIds(newStatus);

    if (specificUserIds?.length > 0) {
      // Use specifically assigned users
      recipientUserIds = [...specificUserIds];
      // For 'Paid' stage, also include ticket creator and sales owner
      if (newStatus === 'Paid') {
        if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
        if (salesOwnerUserId) recipientUserIds?.push(salesOwnerUserId);
        recipientUserIds = [...recipientUserIds, ...(projectMemberUserIds || [])];
      }
    } else {
      // ── Step 2: Fall back to role-based notification ──────────────────────
      switch (newStatus) {
        case 'Pending Team Assignment': {
          const pmUsers = await fetchUsersByRole(['project_manager', 'admin', 'super_admin']);
          recipientUserIds = [...pmUsers];
          if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
          break;
        }
        case 'Calculated': {
          const pmUsers = await fetchUsersByRole(['project_manager', 'admin', 'super_admin']);
          recipientUserIds = [...pmUsers];
          if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
          break;
        }
        case 'Pending Approval': {
          const approvers = await fetchUsersByRole(['approver', 'admin', 'super_admin']);
          recipientUserIds = [...approvers];
          break;
        }
        case 'Approved': {
          const financeUsers = await fetchUsersByRole(['finance', 'admin', 'super_admin']);
          recipientUserIds = [...financeUsers];
          if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
          break;
        }
        case 'Ready for Payment': {
          const financeUsers = await fetchUsersByRole(['finance', 'admin', 'super_admin']);
          recipientUserIds = [...financeUsers];
          break;
        }
        case 'Paid': {
          if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
          if (salesOwnerUserId) recipientUserIds?.push(salesOwnerUserId);
          recipientUserIds = [...recipientUserIds, ...(projectMemberUserIds || [])];
          break;
        }
        case 'Rejected': case'On Hold': {
          const pmUsers = await fetchUsersByRole(['project_manager', 'admin', 'super_admin']);
          recipientUserIds = [...pmUsers];
          if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
          break;
        }
        default:
          return;
      }
    }

    if (!recipientUserIds?.length) return;

    await createNotificationsForUsers({
      ticketId,
      ticketNumber,
      projectName,
      recipientUserIds,
      triggeredByUserId,
      stage: newStatus,
    });
  } catch (err) {
    // Notifications must never break the main workflow
    console.warn('[Notifications] triggerWorkflowNotification error:', err?.message);
  }
};

// ============================================================
// TRIGGER ROLLBACK NOTIFICATION
// Notifies responsible users that a ticket has been rolled back
// ============================================================

export const triggerRollbackNotification = async ({
  ticketId,
  ticketNumber,
  projectName,
  fromStatus,
  toStatus,
  reason,
  triggeredByUserId,
  ticketCreatorId = null,
  salesOwnerUserId = null,
}) => {
  try {
    const message = `Ticket ${ticketNumber} for "${projectName}" has been rolled back from "${fromStatus}" to "${toStatus}". Reason: ${reason}. Please review and take the necessary action.`;

    // Collect recipients: admins, project managers, ticket creator, sales owner
    const adminPmUsers = await fetchUsersByRole(['admin', 'super_admin', 'project_manager', 'approver', 'finance']);
    let recipientUserIds = [...adminPmUsers];
    if (ticketCreatorId) recipientUserIds?.push(ticketCreatorId);
    if (salesOwnerUserId) recipientUserIds?.push(salesOwnerUserId);

    // Deduplicate and exclude the person who triggered the rollback
    const uniqueRecipients = [...new Set(recipientUserIds)]?.filter(
      uid => uid && uid !== triggeredByUserId
    );

    if (!uniqueRecipients?.length) return;

    const actionUrl = `/incentive-tickets/${ticketId}`;
    const rows = uniqueRecipients?.map(uid => ({
      ticket_id: ticketId,
      ticket_number: ticketNumber,
      project_name: projectName,
      recipient_user_id: uid,
      triggered_by_user_id: triggeredByUserId || null,
      stage: toStatus,
      message,
      action_label: 'Review Ticket',
      action_url: actionUrl,
      is_read: false,
    }));

    const { error } = await supabase
      ?.from('incentive_notifications')
      ?.insert(rows);

    if (error) console.warn('[Notifications] Rollback notification insert error:', error?.message);
  } catch (err) {
    console.warn('[Notifications] triggerRollbackNotification error:', err?.message);
  }
};
